import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { ActivityHeatmapGrid } from "./activity-heatmap-grid"

interface ActivityHeatmapModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    defaultTab?: "activity" | "development"
}

export function ActivityHeatmapModal({ open, onOpenChange, defaultTab = "activity" }: ActivityHeatmapModalProps) {
    const [activeTab, setActiveTab] = useState<"activity" | "development">(defaultTab)

    const { data: activityData, isLoading: activityLoading } = useQuery({
        queryKey: ["activity-heatmap", 365],
        queryFn: () => api.activityHeatmap.getActivity(365),
        staleTime: 5 * 60 * 1000,
        enabled: open,
    })

    const { data: commitsData, isLoading: commitsLoading } = useQuery({
        queryKey: ["commits-heatmap", 365],
        queryFn: () => api.activityHeatmap.getCommits(365),
        staleTime: 5 * 60 * 1000,
        enabled: open && activeTab === "development",
    })

    const currentData = activeTab === "activity" ? activityData : commitsData
    const isLoading = activeTab === "activity" ? activityLoading : commitsLoading
    const label = activeTab === "activity" ? "actions" : "commits"

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle>Activity — Last 365 Days</DialogTitle>
                        <div className="flex items-center gap-1 p-0.5 bg-muted rounded-lg">
                            <button
                                onClick={() => setActiveTab("activity")}
                                className={cn(
                                    "px-3 py-1 text-xs font-medium rounded-md transition-colors",
                                    activeTab === "activity"
                                        ? "bg-background text-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                Activity
                            </button>
                            <button
                                onClick={() => setActiveTab("development")}
                                className={cn(
                                    "px-3 py-1 text-xs font-medium rounded-md transition-colors",
                                    activeTab === "development"
                                        ? "bg-background text-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                Development
                            </button>
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="text-sm text-muted-foreground">Loading...</div>
                        </div>
                    ) : currentData ? (
                        <div className="space-y-4">
                            <ActivityHeatmapGrid
                                data={currentData.data}
                                days={365}
                                label={label}
                            />

                            <div className="flex items-center gap-6 text-sm text-muted-foreground pt-2 border-t">
                                <span>
                                    <span className="font-semibold text-foreground text-lg">{currentData.total.toLocaleString()}</span> total {label}
                                </span>
                                <span>
                                    <span className="font-semibold text-foreground text-lg">{currentData.active_days}</span> active days
                                </span>
                            </div>
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    )
}
